import { Upload } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { DISPLAY_TYPES } from 'components/common/UploadModal/utils';
import { renderAllowedMediaTooltip } from 'components/common/UploadModal/utils/renderAllowedMediaTooltip';

import useMediaRules from 'hooks/useMediaRules';
import { Tooltip } from 'ui/base';

import MediaType from './components/MediaType';
import { draggerStyle, infoStyle, OptionPanel } from './components/ui.style';

const CONFIG = {
  [DISPLAY_TYPES.COMPUTER]: {
    autoBrowse: true,
    icon: <i className="fb-icon--computer" />,
    legend: <FormattedMessage id="mediaLibrary.icons.browse" defaultMessage="Browse" />,
  },
  [DISPLAY_TYPES.LIBRARY]: {
    icon: <i className="fb-icon--library" />,
    legend: <FormattedMessage id="mediaLibrary.icons.library" defaultMessage="Photo library" />,
  },
  [DISPLAY_TYPES.TAKE_PICTURE]: {
    icon: <i className="fb-icon--camera-add" />,
    legend: <FormattedMessage id="mediaLibrary.icons.picture" defaultMessage="Take picture" />,
  },
};

/**
 * @type {React.FC<{
 *   allowed: import("hooks/useMediaRules").AllowedMedias,
 *   displayTypes: import("components/common/UploadModal/utils").DisplayTypeEnum[],
 *   onClick?: (displayType: import("components/common/UploadModal/utils").DisplayTypeEnum, autoBrowse: boolean ) => void,
 * } & import("antd/es/upload").DraggerProps>}
 * */
const CoreDragger = ({ allowed, disabled, onClick, displayTypes = [], displayMaxSizeMessage, className, ...draggerProps }) => {
  const { extensions, config, fileTypes } = useMediaRules(allowed);

  const accept = React.useMemo(
    // Force `.` at the beginning of the extension name
    () => extensions?.map(extension => `.${extension.replace(/^\.?/, '')}`).join(',') ?? fileTypes.join(','),
    [fileTypes, extensions]
  );

  const handleMediaLibraryOnClick = React.useCallback(
    (displayType, autoBrowse = false) =>
      event => {
        onClick?.(event, displayType, autoBrowse);
      },
    [onClick]
  );

  const tooltip = renderAllowedMediaTooltip(extensions, config, displayMaxSizeMessage);

  return (
    <Upload.Dragger
      showUploadList={false}
      accept={accept}
      disabled={disabled}
      className={classNames(className, draggerStyle, 'p-0')}
      {...draggerProps}
    >
      <div className="dragger-content p-4 d-flex flex-column align-items-center justify-content-around position-relative h-100">
        <span className="text-secondary font-weight-bold mb-3">
          <FormattedMessage id="page.fiche.medias.drop_here" defaultMessage="Drag & drop media here" />,
          <span className="ml-1 text-lowercase">
            <FormattedMessage id="or" defaultMessage="Or" />
          </span>
        </span>

        <OptionPanel size={displayTypes.length}>
          {displayTypes.map(type => (
            <MediaType
              key={type}
              disabled={disabled}
              data-cy={`media-library-uploader-${type}`}
              onClick={handleMediaLibraryOnClick(type, CONFIG[type].autoBrowse)}
              {...CONFIG[type]}
            />
          ))}
        </OptionPanel>

        {tooltip && (
          <Tooltip placement="left" title={tooltip}>
            <div className={classNames(infoStyle, 'fb-icon--info text-secondary position-absolute')} />
          </Tooltip>
        )}
      </div>
    </Upload.Dragger>
  );
};

export default CoreDragger;
